"use client";

import { useState, useRef, useEffect } from "react";
import Link from "next/link";

const faqs = [
  {
    q: "How quickly can you start managing my property?",
    a: "We can typically onboard a new property within 5–7 business days. This includes a property walkthrough, professional photography, listing optimization, and setting up our management systems. For cleaning-only clients, we can usually start within 48 hours.",
  },
  {
    q: "What\'s included in the turnover cleaning service?",
    a: "Our 5-star turnover includes: full linen and towel replacement, toiletry restocking, complete kitchen and appliance cleaning, bathroom deep clean, vacuuming and mopping all floors, baseboard and vent cleaning, remote battery checks, and guest-ready staging. We follow a 47-point checklist on every clean.",
  },
  {
    q: "How does your pricing compare to other PCB management companies?",
    a: "Our flat management fee of 18% is one of the most competitive in the PCB market. Unlike some companies that charge 30–35%, we keep our fee low by operating efficiently. More importantly, our dynamic pricing strategy typically increases your gross revenue by 20–35%, meaning you net more even after our fee.",
  },
  {
    q: "Do I lose control of my property under full management?",
    a: "Absolutely not. You maintain full ownership and control. You can block dates for personal use anytime. We provide monthly reports so you always know what's happening. You're consulted on any maintenance decisions over $500. Think of us as your local expert partner, not a replacement.",
  },
  {
    q: "What happens if a guest damages my property?",
    a: "We document property condition before and after every stay with photos. In the event of damage, we handle the Airbnb/VRBO resolution process on your behalf, file claims, coordinate repairs, and have the property back in service as quickly as possible. We work with vetted local contractors for fast turnarounds.",
  },
  {
    q: "Do you work with properties outside Panama City Beach proper?",
    a: "We primarily serve Panama City Beach, but also cover nearby areas including Inlet Beach, Rosemary Beach, and parts of Panama City. Contact us with your property address and we'll confirm coverage.",
  },
  {
    q: "What is the cancellation policy for guests?",
    a: "Cancellations more than 30 days before check-in receive a full refund minus the cleaning fee. Cancellations 8–30 days out receive a 50% refund; the cleaning fee is non-refundable. Cancellations within 7 days of check-in receive no refund. The cleaning fee is non-refundable in all cases.",
  },
];

function FAQItem({ q, a, index }: { q: string; a: string; index: number }) {
  const [open, setOpen] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  return (
    <div
      className={`border rounded-2xl overflow-hidden transition-all duration-300 ${
        open ? "border-gold/40 bg-gold/3" : "border-sand-medium bg-white hover:border-navy/20"
      }`}
    >
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between p-6 text-left gap-4"
        aria-expanded={open}
      >
        <div className="flex items-center gap-4">
          <span className="w-7 h-7 rounded-full bg-navy/8 text-navy text-xs font-bold flex items-center justify-center shrink-0">
            {String(index + 1).padStart(2, "0")}
          </span>
          <span className="font-semibold text-navy text-sm sm:text-base">{q}</span>
        </div>
        <div className={`w-7 h-7 rounded-full border-2 flex items-center justify-center shrink-0 transition-all duration-300 ${
          open ? "border-gold bg-gold rotate-45" : "border-navy/20 bg-transparent"
        }`}>
          <svg className={`w-3 h-3 transition-colors ${open ? "text-navy" : "text-navy/50"}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
          </svg>
        </div>
      </button>
      <div
        ref={contentRef}
        className="overflow-hidden transition-all duration-400"
        style={{
          maxHeight: open ? `${contentRef.current?.scrollHeight ?? 500}px` : "0",
          opacity: open ? 1 : 0,
        }}
      >
        <p className="px-6 pb-6 text-navy/65 text-sm leading-relaxed pl-[4.5rem]">{a}</p>
      </div>
    </div>
  );
}

export default function FAQSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add("revealed");
        });
      },
      { threshold: 0.1 }
    );
    const elements = sectionRef.current?.querySelectorAll(".reveal-hidden");
    elements?.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="section-padding bg-sand-dark">
      <div className="max-w-4xl mx-auto px-5 sm:px-8 lg:px-12">
        <div className="text-center mb-14 reveal-hidden">
          <span className="text-gold text-xs font-bold uppercase tracking-widest block mb-3">FAQ</span>
          <h2 className="font-heading text-4xl sm:text-5xl text-navy mb-5">
            Questions from{" "}
            <span className="italic font-light">Property Owners</span>
          </h2>
          <p className="text-navy/55 text-base max-w-xl mx-auto">
            Everything you need to know before partnering with Premier Property MGT.
          </p>
        </div>

        <div className="flex flex-col gap-3 mb-12">
          {faqs.map((faq, i) => (
            <div key={i} className="reveal-hidden" style={{ transitionDelay: `${i * 80}ms` }}>
              <FAQItem q={faq.q} a={faq.a} index={i} />
            </div>
          ))}
        </div>

        <div className="text-center reveal-hidden">
          <p className="text-navy/50 text-sm mb-4">Still have questions?</p>
          <Link
            href="/contact"
            className="btn-navy inline-flex items-center gap-2 px-8 py-3.5 rounded-full font-semibold text-sm"
          >
            Contact Our Team
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </Link>
        </div>
      </div>
    </section>
  );
}